import { useEffect, useState } from "react";
import { api, ApiError } from "../../api/client";
import { Modal } from "../../components/ui/Modal";
import type { Permission, UserListItem } from "../../types";

/**
 * Individuelle Berechtigungen eines Benutzers verwalten (grant/revoke
 * zusätzlich zu den Standardrechten der Rolle).
 */
export function UserPermissionsModal({ user, onClose }: { user: UserListItem; onClose: () => void }) {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [overrides, setOverrides] = useState<Record<string, "grant" | "revoke" | null>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get<Permission[]>("/api/roles/permissions"),
      api.get<{ permissionOverrides: { key: string; effective: number }[] }>(`/api/users/${user.id}`),
    ]).then(([perms, detail]) => {
      setPermissions(perms);
      const map: Record<string, "grant" | "revoke"> = {};
      for (const o of detail.permissionOverrides ?? []) map[o.key] = o.effective ? "grant" : "revoke";
      setOverrides(map);
    }).finally(() => setLoading(false));
  }, [user.id]);

  function cycle(key: string) {
    setOverrides((prev) => {
      const current = prev[key] ?? null;
      const next = current === null ? "grant" : current === "grant" ? "revoke" : null;
      return { ...prev, [key]: next };
    });
  }

  async function handleSave() {
    setSubmitting(true);
    setError(null);
    try {
      const payload = Object.entries(overrides)
        .filter(([, v]) => v !== null)
        .map(([permissionKey, v]) => ({ permissionKey, effective: v === "grant" }));
      await api.put(`/api/users/${user.id}/permissions`, { overrides: payload });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  const grouped = permissions.reduce<Record<string, Permission[]>>((acc, p) => {
    (acc[p.category] ??= []).push(p);
    return acc;
  }, {});

  return (
    <Modal
      title={`Individuelle Rechte: ${user.full_name}`}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="btn-primary" onClick={handleSave} disabled={submitting || loading}>Speichern</button>
        </>
      }
    >
      {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2 mb-3">{error}</div>}
      <p className="text-xs text-gray-500 mb-4">
        Klicken zum Umschalten: <span className="text-gray-400">Rollenstandard</span> → <span className="text-success">zusätzlich gewährt</span> → <span className="text-danger">entzogen</span>
      </p>
      {loading ? (
        <p className="text-sm text-gray-500">Lädt…</p>
      ) : (
        <div className="space-y-5">
          {Object.entries(grouped).map(([category, perms]) => (
            <div key={category}>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">{category}</h4>
              <div className="grid sm:grid-cols-2 gap-2">
                {perms.map((p) => {
                  const state = overrides[p.key] ?? null;
                  const tone = state === "grant" ? "bg-success-muted text-success border-success/30" : state === "revoke" ? "bg-danger-muted text-danger border-danger/30" : "bg-surface-raised text-gray-300 border-surface-border";
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => cycle(p.key)}
                      className={`text-left text-sm px-3 py-2 rounded-lg border transition-colors ${tone}`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
